import React, { useEffect, useState, useMemo } from "react";
import "./summaryStationWise.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingOverlay from "react-loading-overlay";
import { SERVER_URL, USE_OVERLAY } from "../config/config";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const StationToWiseSummaryReport = ({ sessionObject }) => {
  const [overlay, setOverlay] = useState(false);
  const [data, setData] = useState([]);
  const [dateState, setDateState] = useState({
    date_from: new Date(),
    date_to: new Date(),
  });

  const columnConfig = [
    { key: "bilty_date", label: "Bilty Date" },
    { key: "pkgs", label: "Packages" },
    { key: "bilty_no_count", label: "No of Bilty" },
    { key: "to_pay", label: "To Pay" },
    { key: "paid", label: "Paid" },
    { key: "to_be_billed", label: "To Be Billed" },
    { key: "bilty_charge", label: "Bilty Charge" },
    { key: "hamali", label: "Hamali" },
    { key: "door_del_charge", label: "DD Charge" },
    { key: "lc_charge", label: "Collection Charge" },
    { key: "other_amount", label: "Other Amount" },
    { key: "total_amount", label: "Total Amount" },
  ];

  const fetchData = () => {
    setOverlay(true);
    const payload = {
      date_dict: {
        date_from: dateState.date_from,
        date_to: dateState.date_to,
      },
    };
    fetch(SERVER_URL + "/report/admin_lr", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        setData(data.data || []);
        setOverlay(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setOverlay(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [dateState]);

  const initializeTotal = () => ({
    pkgs: 0,
    bilty_charge: 0,
    hamali: 0,
    door_del_charge: 0,
    bilty_no_count: 0,
    total_amount: 0,
    lc_charge: 0,
    other_amount: 0,
    paid: 0,
    to_pay: 0,
    to_be_billed: 0,
  });

  const updateTotals = (total, item) => {
    total.pkgs += Number(item.pkgs || 0);
    total.bilty_charge += Number(item.bilty_charge || 0);
    total.hamali += Number(item.hamali || 0);
    total.door_del_charge += Number(item.door_del_charge || 0);
    total.bilty_no_count += 1;
    total.total_amount += Number(item.total_amount || 0);
    total.lc_charge += Number(item.lc_charge || 0);
    total.other_amount += Number(item.other_amount || 0);
    total.paid += item.pay_type === 2 ? Number(item.freight || 0) : 0;
    total.to_pay += item.pay_type === 1 ? Number(item.freight || 0) : 0;
    total.to_be_billed += item.pay_type === 4 ? Number(item.freight || 0) : 0;

    Object.keys(total).forEach((key) => {
      if (typeof total[key] === "number") {
        total[key] = parseFloat(total[key].toFixed(2));
      }
    });
  };

  const processedData = useMemo(() => {
    const groupedData = {};
    const dateSet = new Set();

    data.forEach((item) => {
      const stationTo = (item.station_to_name || "").trim().toUpperCase();
      const date = item.bilty_date || "Unknown";

      if (!stationTo) return;

      dateSet.add(date);

      if (!groupedData[stationTo])
        groupedData[stationTo] = { total: initializeTotal(), items: {} };

      if (!groupedData[stationTo].items[date]) {
        groupedData[stationTo].items[date] = {
          bilty_date: date,
          ...initializeTotal(),
        };
      }

      updateTotals(groupedData[stationTo].items[date], item);
      updateTotals(groupedData[stationTo].total, item);
    });

    const allDates = Array.from(dateSet);
    allDates.forEach((date) => {
      Object.keys(groupedData).forEach((stationTo) => {
        if (!groupedData[stationTo].items[date]) {
          groupedData[stationTo].items[date] = {
            bilty_date: date,
            ...initializeTotal(),
          };
        }
      });
    });

    return groupedData;
  }, [data, dateState]);

  const grandTotal = useMemo(() => {
    const total = initializeTotal();
    data.forEach((item) => updateTotals(total, item));
    return total;
  }, [data]);

  const formatDate = (date) => {
    const d = date.getDate().toString().padStart(2, "0");
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "React App";
    const sheet = workbook.addWorksheet("Station To Summary");

    // Add transporter name, report type, and date range
    const transporterName =
      process.env.REACT_APP_CLIENT_NAME || "Default Transporter";
    const reportType = "Station To Wise Summary Report";
    const dateFrom = formatDate(dateState.date_from);
    const dateTo = formatDate(dateState.date_to);

    const row1 = sheet.addRow([transporterName]);
    const row2 = sheet.addRow([reportType]);
    const row3 = sheet.addRow([`Date From:`, dateFrom, `Date To:`, dateTo]);

    [row1, row2, row3].forEach((row) => {
      row.eachCell((cell) => {
        cell.font = { bold: true };
      });
    });

    const addBorderToRow = (row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    };

    sheet.addRow([]);

    // Header row
    const headers = [
      "Destination",
      "Sr No.",
      ...columnConfig.map((col) => col.label),
    ];
    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });
    addBorderToRow(headerRow);

    // data rows
    Object.entries(processedData).forEach(([stationTo, stationData]) => {
      const destinationRow = sheet.addRow([stationTo]);
      destinationRow.eachCell((cell) => {
        cell.font = { bold: true };
      });
      addBorderToRow(destinationRow);

      // Item rows
      const itemRows = Object.values(stationData.items);
      itemRows.forEach((item, index) => {
        const rowData = [
          "",
          index + 1,
          ...columnConfig.map((col) => item[col.key] ?? 0),
        ];
        const row = sheet.addRow(rowData);
        addBorderToRow(row);
      });

      // Total row
      const totalRowData = [
        "",
        "Total",
        ...columnConfig.map((col) => stationData.total[col.key] ?? 0),
      ];
      const totalRow = sheet.addRow(totalRowData);
      totalRow.eachCell((cell) => {
        cell.font = { bold: true };
      });
      addBorderToRow(totalRow);

      sheet.addRow([]);
    });

    // Grand Total Title row
    const grandTotalTitleRow = sheet.addRow(["Grand Total"]);
    grandTotalTitleRow.eachCell((cell) => {
      cell.font = { bold: true };
    });
    addBorderToRow(grandTotalTitleRow);

    // Grand Total row
    const grandTotalRowData = [
      "",
      "Grand Total",
      ...columnConfig.map((col) => grandTotal[col.key] ?? 0),
    ];
    const grandTotalRow = sheet.addRow(grandTotalRowData);
    grandTotalRow.eachCell((cell) => {
      cell.font = { bold: true };
    });
    addBorderToRow(grandTotalRow);

    // Auto-fit columns
    sheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const length = cell.value ? cell.value.toString().length : 0;
        if (length > maxLength) {
          maxLength = length;
        }
      });
      column.width = maxLength + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "station_to_summary.xlsx");
  };

  return (
    <>
      <div className="summaryContainer">
        {USE_OVERLAY && (
          <LoadingOverlay
            active={overlay}
            spinner
            text="Loading your content..."
            styles={{ wrapper: { overflow: "hidden" } }}
          />
        )}
        <div className="dateHolder">
          <div>
            From Date:{" "}
            <DatePicker
              dateFormat="dd-MM-yyyy"
              selected={dateState.date_from}
              onChange={(date) =>
                setDateState({ ...dateState, date_from: date })
              }
            />
          </div>
          <div>
            To Date:{" "}
            <DatePicker
              dateFormat="dd-MM-yyyy"
              selected={dateState.date_to}
              onChange={(date) => setDateState({ ...dateState, date_to: date })}
            />
          </div>
        </div>

        <div className="titleHolder">
          <span>Destination Wise Booking Summary Report (Station To)</span>
        </div>

        <div className="summaryTable">
          {Object.keys(processedData).map((stationTo) => {
            const stationGroup = processedData[stationTo];
            const itemGroups = Object.values(stationGroup.items);

            return (
              <div key={stationTo} className="station-section">
                <div className="branchTitleHolder">
                  <span className="branchTitle">Destination: {stationTo}</span>
                </div>
                <table id="tabled">
                  <thead>
                    <tr>
                      <th>Sr No.</th>
                      {columnConfig.map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {itemGroups.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        {columnConfig.map((col) => (
                          <td key={col.key}>
                            {item[col.key] || item[col.key] === 0
                              ? item[col.key]
                              : 0}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td>Total</td>
                      {columnConfig.map((col) => (
                        <td key={col.key}>
                          {stationGroup.total[col.key] ||
                          stationGroup.total[col.key] === 0
                            ? stationGroup.total[col.key]
                            : 0}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* ✅ Grand Total Section */}
          <div className="grand-total-section">
            <div className="branchTitleHolder">
              <span className="branchTitle">Grand Total</span>
            </div>
            <table id="tabled">
              <thead>
                <tr>
                  <th>Title</th>
                  {columnConfig.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="total-row">
                  <td>Grand Total</td>
                  {columnConfig.map((col) => (
                    <td key={col.key}>
                      {grandTotal[col.key] || grandTotal[col.key] === 0
                        ? grandTotal[col.key]
                        : 0}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="download-btn-div">
        <button className="download-btn" onClick={handleDownloadExcel}>
          Download as XLS
        </button>
      </div>
    </>
  );
};

export default StationToWiseSummaryReport;
